import qs from "qs";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
// Swiper styles
import { useAuth0 } from "@auth0/auth0-react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { ko } from "date-fns/locale";
import { throttle } from "lodash";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import QRCode from "react-qr-code";
import { useRecoilValue } from "recoil";
import { getCommonFacilityListAsync } from "src/api/building/building-api";
import { CommonFacilityListModel } from "src/api/building/building-types";
import { getContractApply } from "src/api/contract/contract-api";
import { useApiOperation } from "src/api/hooks";
import { getReservationListAsync } from "src/api/reservation/reservation-api";
import { ReservationListModel } from "src/api/reservation/reservation-types";
import { useI18n } from "src/hooks/useI18n";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import { globalPartnerState } from "src/recoil/partners/atom";
import { useToast } from "src/recoil/toast/hook";
import { useAutoReloadOnUpdate } from "src/update/useUpdateAvailable";
import { resizedImageUrl, sortByASC, ViewYmdFormat } from "src/utils";
import SwiperCore, { Autoplay, Navigation, Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { Modal, timeSlots } from "../reservation-types";
import CountdownText from "./components/CountdownText";
import ReservationBox from "./components/ReservationBox";
import ReservationDetailModal from "./components/ReservationDetailModal";
import ReservationSettingModal from "./components/ReservationSettingModal";
import useCountdown from "./hooks/useCountdown";
import useMinuteClock from "./hooks/useMinuteClock";

SwiperCore.use([Navigation, Pagination, Autoplay]);
type SwiperType = SwiperCore;

type SearchParams = {
  page?: number;
  size?: number;
};
const defaultParams = {
  sort: {
    orders: [
      {
        property: "id",
        direction: "DESC",
      },
    ],
  },
};

// 상수
const TIME_INTERVAL = 30000; // 데이터 호출 주기 30초
const AUTOPLAY_DELAY = 30000; // 오토플레이 주기 30초
const MODAL_INACTIVITY_TIMEOUT = 60000; // 유저 인터랙션감지 1분
const PIXEL_PER_BLOCK = 45; // 한 칸(30분)은 45px
const SLIDE_SPEED = 500; // 애니메이션속도
const SAFE_MARGIN = 5; // 뷰포트 경계선으로부터의 마진 내에서는 휠 무시
const DATE_INACTIVITY_TIMEOUT = 30000; // 날짜 선택 후 30초 동안 비활성시 오늘로 복원
const ITEMS_PER_PAGE = 6; // 한 페이지(슬라이드)에 표시할 시설 수
const MAX_FACILITIES_PER_REQUEST = 100; // 예약 조회 1회당 시설 수
const RESERVATION_QUERY_KEY = "reservationCalendar"; // 예약 조회 queryKey prefix (이 페이지 전용)
const HANGING_FETCH_THRESHOLD = TIME_INTERVAL / 2; // 데이터 갱신 시점에 이 시간 이상 진행 중인 요청은 응답 없이 멈춘 것으로 보고 다시 조회

// 시설 목록을 한 페이지 단위(6개)로 나눈 이중배열. 모자란 칸은 빈 객체로 채움
const createChunks = (facilities: CommonFacilityListModel[]): CommonFacilityListModel[][] => {
  if (!facilities.length) {
    return [Array(ITEMS_PER_PAGE).fill({} as CommonFacilityListModel)];
  }

  return Array.from({ length: Math.ceil(facilities.length / ITEMS_PER_PAGE) }, (_, index) => {
    const start = index * ITEMS_PER_PAGE;
    const chunk = facilities.slice(start, start + ITEMS_PER_PAGE);

    return [...chunk, ...Array(ITEMS_PER_PAGE - chunk.length).fill({} as CommonFacilityListModel)];
  });
};

type ReservationChunksResult = {
  chunks: CommonFacilityListModel[][];
  failed: boolean; // 조회 실패 시 빈 화면을 표시하고 페이지 보정을 건너뜀
};

const ReservationCalendar = () => {
  const { user } = useAuth0();

  const { isAuthority, isLoading: isAuthorityLoading, authorityStatus } = usePartnerAuthority();
  const { openToast } = useToast();
  const currentTime = useMinuteClock(); // 현재 시각 라벨·타임라인 기준 (매 분 정각 갱신)
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date()); // 선택된 날짜 상태
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false); // 날짜 피커 표시 상태

  const [reservationDetailModal, setReservationDetailModal] = useState<Modal>({ isOpen: false });
  const [reservationSettingModal, setReservationSettingModal] = useState<Modal>({ isOpen: false });
  const [facilityList, setFacilityList] = useState<CommonFacilityListModel[]>([]);
  const [isManualNavigation, setIsManualNavigation] = useState(false);
  const browserLanguage = navigator.language.split("-")[0];

  // Ref
  const scrollContainerRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const initializedScrollContainersRef = useRef(new WeakSet<HTMLDivElement>()); // 최초 스크롤 위치를 맞춘 스크롤 영역

  useEffect(() => {
    if (!isAuthorityLoading && isAuthority("r") === false && authorityStatus === "success") {
      console.error("권한없음 : 접근 권한이 제한되었습니다. 관리자에게 문의하세요.\n");
    }
  }, [isAuthority, isAuthorityLoading, authorityStatus]);

  // Hooks
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const partner = useRecoilValue(globalPartnerState);

  // 새 버전 배포 시 자동 새로고침
  useAutoReloadOnUpdate({ pollIntervalMs: 2 * 60 * 1000 });

  const { executeAsync: getCommonFacilityList } = useApiOperation(getCommonFacilityListAsync, { noLoading: true, noErrorModal: true });
  const { executeAsync: getReservationList } = useApiOperation(getReservationListAsync, { noLoading: true, noErrorModal: true });
  const { executeAsync: getContractList } = useApiOperation(getContractApply, { noLoading: true, noErrorModal: true });

  const queryParams: any = useMemo(() => {
    const _queryParams: any = qs.parse(location.search, {
      ignoreQueryPrefix: true,
      allowDots: true,
    });
    return {
      ..._queryParams,
    };
  }, [location]);

  const isAutoplayEnabled = queryParams.transportType === "on";

  // 현재 타임라인 위치 계산 (선택된 날짜가 오늘인 경우에만)
  const getCurrentTimePosition = useMemo(() => {
    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();

    const pixelsPerMinute = PIXEL_PER_BLOCK / 30; // 1분당 픽셀 = 45px / 30분
    const hoursInPixels = hours * (PIXEL_PER_BLOCK * 2);
    const minutesInPixels = minutes * pixelsPerMinute;

    return PIXEL_PER_BLOCK + hoursInPixels + minutesInPixels - 1; // 보더 오차 1픽셀 빼주기
  }, [currentTime]);

  // ================================ Sentry Log ================================
  // =========================== 센트리 부하 증가로 인터벌 로그 전송 주석처리   ================================

  // 30s 인터벌 로그
  // const sentryIntervalLog = (additionalData?: any, status?: "success" | "failed") => {
  //   Sentry.captureMessage(`공용공간예약 현황-30s 인터벌 로그`, {
  //     // level: status === "success" ? "info" : "error", // level: 로그의 심각도 (debug, info, warning, error, fatal)
  //     level: "info", // level: 로그의 심각도 (debug, info, warning, error, fatal)
  //     fingerprint: ["calendar-interval-30s"], // fingerprint: 이벤트 그룹핑 강제 지정 / 같은 fingerprint = 같은 이슈로 묶임 (Issues에서 하나로 표시, Events 숫자만 증가)
  //     tags: {
  //       component: "ReservationCalendar",
  //       keyword: "CALENDAR_INTERVAL",
  //       status: status, // status: Discover에서 컬럼으로 표시되어 success/failed 구분 가능 / 필터링도 가능 (예: status:failed로 에러만 검색)
  //     },
  //     // extra: 상세 정보 저장 (이벤트 클릭시 세부사항에서 확인 가능)
  //     extra: {
  //       userEmail: user?.email,
  //       partner: { partnerId: partner?.id, code: partner?.code },
  //       apiCallTimeUTC: moment().utc().format(),
  //       apiCallTimeKST: moment().format("YYYY-MM-DD HH:mm:ss"),
  //       ...additionalData,
  //       apiCallStatus: status,
  //     },
  //   });
  // };

  // const sentryNetworkErrorLog = (error: any, apiName: string, additionalData?: any) => {
  //   const networkInfo = {
  //     isOnline: navigator.onLine,
  //     userAgent: navigator.userAgent,
  //     language: navigator.language,
  //     connection: (navigator as any).connection
  //       ? {
  //           effectiveType: (navigator as any).connection.effectiveType,
  //           downlink: (navigator as any).connection.downlink,
  //           rtt: (navigator as any).connection.rtt,
  //         }
  //       : null,
  //   };

  //   Sentry.captureMessage("공용공간예약 현황-네트워크 에러", {
  //     level: "info",
  //     tags: {
  //       component: "ReservationCalendar",
  //       keyword: "CALENDAR_NETWORK_ERROR",
  //       partnerId: partner?.id?.toString(),
  //       apiName: apiName,
  //       networkStatus: navigator.onLine ? "online" : "offline",
  //     },
  //     extra: {
  //       errorTimeUTC: moment().utc().format(),
  //       errorTimeKST: moment().format("YYYY-MM-DD HH:mm:ss"),
  //       userEmail: user?.email,
  //       partner: partner,

  //       // 에러 정보
  //       errorType: error?.code || error?.name || "unknown",
  //       errorMessage: error?.message,

  //       // 네트워크 상세 정보
  //       networkInfo: networkInfo,

  //       // 추가 데이터
  //       ...additionalData,
  //     },
  //   });
  // };

  // 401 에러 로그 > AuthInitializer.tsx > setAxiosResponseInterceptor 에서 처리

  // ================================ Sentry Log ================================

  // 쿼리파라미터 정보와 함께 네비게이션
  const navigateWithQueryParams = useCallback(
    (passParams?: SearchParams) => {
      const newQueryParams = passParams ? { ...passParams } : { ...queryParams };
      const filteredRest: any = {};

      Object.keys(newQueryParams).forEach((key) => {
        const value = newQueryParams[key as keyof typeof newQueryParams];
        if (value !== 0 && value !== "" && value != null) {
          filteredRest[key] = value;
        }
      });

      const newQueryParamStr = qs.stringify(filteredRest, { allowDots: true, encode: true });
      navigate(location.pathname + "?" + decodeURIComponent(newQueryParamStr));
    },
    [navigate, location.pathname, queryParams],
  );

  // 공용공간 호출
  // ⚠️ 이 함수는 마운트 시 1회만 호출되는 것이 의도이므로 queryParams를 deps에 일부러 포함하지 않는다.
  //    (queryParams를 추가하면 URL 변경마다 시설 목록을 매번 재조회하게 되어 동작이 바뀜)
  //    이후의 필터(buildingId/floor/facilityType)는 fetchReservationChunks 단계에서 클라이언트 필터링으로 처리됨.
  const fetchCommonfacility = useCallback(async () => {
    const { data: facilityData } = await getCommonFacilityList(
      queryParams.facilityIdList ? { ...defaultParams, buildingCommonFacilityIdList: queryParams.facilityIdList } : defaultParams,
    );
    if (facilityData?.data?.content) {
      setFacilityList(facilityData.data.content);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCommonFacilityList]);

  // 예약 호출 및 매핑
  // 필터된 전체 시설의 예약을 조회해 페이지 단위로 나눈 결과를 반환한다. (state 원본 facilityList는 수정하지 않음)
  const fetchReservationChunks = async (signal: AbortSignal): Promise<ReservationChunksResult> => {
    try {
      let filteredFacilityList = sortByASC([...facilityList], "facilityName").filter((facility) => !facility.isDeleted);

      if (queryParams.buildingIdList) {
        filteredFacilityList = filteredFacilityList.filter((facility) => Number(facility.buildingId) === Number(queryParams.buildingIdList));
      }
      if (queryParams.commonFacilityTypeList) {
        filteredFacilityList = filteredFacilityList.filter((facility) => facility.commonFacilityType === queryParams.commonFacilityTypeList);
      }
      if (queryParams.floor) {
        filteredFacilityList = filteredFacilityList.filter((facility) => {
          if (Number(queryParams.floor) > 0) {
            return facility.isGround === true && facility.floorNum === Math.abs(Number(queryParams.floor));
          } else {
            return facility.isGround === false && facility.floorNum === Math.abs(Number(queryParams.floor));
          }
        });
      }

      const facilityIdArray = filteredFacilityList?.map((item) => item.id);
      // 100개씩 나누기
      const facilityIdChunks = [];

      for (let i = 0; i < facilityIdArray.length; i += MAX_FACILITIES_PER_REQUEST) {
        facilityIdChunks.push(facilityIdArray.slice(i, i + MAX_FACILITIES_PER_REQUEST));
      }

      let allReservations: ReservationListModel[] = [];

      for (const chunk of facilityIdChunks) {
        const facilityIdsString = chunk.join(",");

        // 선택된 날짜 사용 (항상 selectedDate 상태 사용)
        const targetDate = moment(selectedDate);

        const reservationData = await getReservationList({
          facilityId: facilityIdsString,
          greaterThanEqualsEnd: targetDate.clone().startOf("day").format(),
          lessThanEqualsStart: targetDate.clone().endOf("day").format(),
          signal,
        })
          .then(({ data }) => data)
          .catch(() => undefined);

        if (reservationData?.data.content && reservationData.data.content.length > 0) {
          allReservations = [...allReservations, ...reservationData.data.content];
        }
      }

      if (allReservations.length > 0) {
        const contractIds = [...new Set(allReservations.map((reservation) => reservation.contract.id))].join(",");

        const { data: contract } = await getContractList({ contractIds: String(contractIds) });

        // 각 시설에 예약 정보 매핑 (조회 결과에 없는 예약이 남지 않도록 매번 새 객체로 만든다)
        filteredFacilityList = filteredFacilityList.map((facility: any) => {
          const matchingReservations = allReservations.filter((reservation) => reservation.facility.id === facility.id);
          const calculateTimeReservations = matchingReservations.map((reservation) => {
            const startTime = moment(reservation.start);
            const endTime = moment(reservation.end);
            return { ...reservation, minutes: endTime.diff(startTime, "minutes") };
          });
          const ReservationWithMbOrganizationName = calculateTimeReservations.map((reservation) => {
            const findMbOrganization = contract.data.content.find((contract) => contract.contractId === Number(reservation.contract.id));
            return { ...reservation, mbOrganizationName: findMbOrganization?.mbOrganizationName };
          });

          return ReservationWithMbOrganizationName.length > 0 ? { ...facility, reservations: ReservationWithMbOrganizationName } : facility;
        });
      }

      return { chunks: createChunks(filteredFacilityList), failed: false };
    } catch (error) {
      // 조회 실패 시 빈 화면 표시 (취소된 요청의 결과는 react-query가 버린다)
      return { chunks: createChunks([]), failed: true };
    }
  };

  //    queryKey에 currentPage를 넣지 않는다.
  //    조회 결과는 페이지와 무관하게 필터된 전체 시설 기준이며, 페이지 전환마다 재조회하면
  //    30초 데이터 갱신과 겹쳐 직전 요청이 abort된다. (BFF broken pipe 원인, QA-3820)
  const facilityIds = useMemo(() => facilityList.map((facility) => facility.id).join(","), [facilityList]);
  const queryClient = useQueryClient();
  const fetchStartedAtRef = useRef<{ startedAt: number } | null>(null); // 진행 중인 예약 조회의 시작 시각 (멈춘 요청 감지용)

  const {
    data: reservationChunks,
    isFetching: isReservationFetching,
    refetch: refetchReservations,
  } = useQuery({
    queryKey: [
      RESERVATION_QUERY_KEY,
      moment(selectedDate).format("YYYY-MM-DD"),
      facilityIds,
      queryParams.buildingIdList,
      queryParams.commonFacilityTypeList,
      queryParams.floor,
    ],
    queryFn: async ({ signal }) => {
      const fetchToken = { startedAt: Date.now() };
      fetchStartedAtRef.current = fetchToken;
      try {
        return await fetchReservationChunks(signal);
      } finally {
        // 취소된 이전 조회가 늦게 끝나도 새 조회의 시작 시각을 지우지 않도록 자기 토큰일 때만 정리
        if (fetchStartedAtRef.current === fetchToken) {
          fetchStartedAtRef.current = null;
        }
      }
    },
    enabled: facilityList.length > 0,
    placeholderData: keepPreviousData, // 날짜·필터 변경 시 새 응답이 올 때까지 이전 화면 유지
    gcTime: 0, // 이전에 본 날짜로 돌아가도 캐시된 옛 데이터를 먼저 보여주지 않음
    retry: false,
    networkMode: "always", // 오프라인이어도 요청을 보류하지 않고 실패 처리
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    meta: { noErrorModal: true },
  });

  const allFacilityChunks = reservationChunks?.chunks ?? [];
  const totalPages = allFacilityChunks.length || 1;

  // 조회 성공 시 현재 페이지가 전체 페이지 수를 넘으면 1페이지로 이동 (조회 실패 시에는 보정하지 않음)
  useEffect(() => {
    if (!reservationChunks || reservationChunks.failed) return;
    if (currentPage > reservationChunks.chunks.length) {
      setCurrentPage(1);
      if (swiper) {
        swiper.slideTo(0, 0);
      }
    }
    // 조회 결과가 바뀔 때만 확인한다 (페이지 이동마다 확인하지 않음)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationChunks]);

  // 자정 경과 감지: 오늘 날짜를 보고 있었다면 시계가 날짜를 넘기는 시점에 함께 새 날짜로 전환한다
  // (날짜가 바뀌면 queryKey가 바뀌어 오늘 날짜로 새로 조회된다. 탭이 숨겨진 채 자정을 넘긴 경우도 다시 보일 때 처리됨)
  // 화면을 그리기 전에 날짜를 바꿔, 자정에 새 시각선과 어제 날짜가 한 프레임이라도 같이 보이지 않게 한다
  const prevClockRef = useRef<Date>(currentTime);
  useLayoutEffect(() => {
    const prevClock = prevClockRef.current;
    prevClockRef.current = currentTime;
    if (moment(currentTime).isSame(prevClock, "day")) return;

    setSelectedDate((selected) => (moment(selected).isSame(prevClock, "day") ? currentTime : selected));
  }, [currentTime]);

  // 데이터 새로고침 타이머 - 메모리 최적화
  const handleDataRefresh = useCallback(() => {
    const fetchStartedAt = fetchStartedAtRef.current?.startedAt;
    const isFetchHanging = fetchStartedAt !== undefined && Date.now() - fetchStartedAt >= HANGING_FETCH_THRESHOLD;

    if (isFetchHanging) {
      // 응답 없이 멈춘 요청은 취소하고 다시 조회한다 (기존과 동일하게 다음 갱신에서 스스로 복구)
      // 콜백이 이전 key를 들고 있을 수 있어 prefix로 취소한다. 취소 후 새 요청은 중복 호출돼도 재사용되도록 cancelRefetch: false
      void queryClient.cancelQueries({ queryKey: [RESERVATION_QUERY_KEY] }).then(() => refetchReservations({ cancelRefetch: false }));
    } else {
      // 진행 중인 요청이 있으면 취소하지 않고 그 요청을 재사용한다
      void refetchReservations({ cancelRefetch: false });
    }
  }, [refetchReservations, queryClient]);

  // 현재 시각이 스크롤 영역 위에서 1/3 지점에 오도록 하는 scrollTop
  const getCurrentTimeScrollTop = useCallback(
    (scrollContainer: HTMLDivElement) => getCurrentTimePosition - scrollContainer.clientHeight / 3,
    [getCurrentTimePosition],
  );

  // 현재 타임라인 위치로 스크롤 (선택된 날짜와 무관하게 항상 현재 시각 위치로 이동)
  // 보고 있는 슬라이드는 부드럽게, 나머지 슬라이드는 즉시 같은 위치로 맞춰 둔다. (페이지 전환 시 00:00부터 다시 스크롤되지 않도록)
  const scrollToCurrentTime = useCallback(() => {
    const activeIndex = swiper?.activeIndex || 0;

    Object.entries(scrollContainerRefs.current).forEach(([index, scrollContainer]) => {
      if (!scrollContainer) return;
      scrollContainer.scrollTo({
        top: getCurrentTimeScrollTop(scrollContainer),
        behavior: Number(index) === activeIndex ? "smooth" : "auto",
      });
    });
  }, [swiper, getCurrentTimeScrollTop, selectedDate]);
  const scrollToCurrentTimeRef = useRef(scrollToCurrentTime);
  scrollToCurrentTimeRef.current = scrollToCurrentTime;

  // 이동할 슬라이드의 스크롤 위치를 지금 보고 있는 슬라이드와 같게 맞춘다 (전환 중 00:00이 보이지 않도록)
  const syncScrollTop = useCallback(
    (targetIndex: number) => {
      const activeContainer = scrollContainerRefs.current[swiper?.activeIndex || 0];
      const targetContainer = scrollContainerRefs.current[targetIndex];
      if (activeContainer && targetContainer && activeContainer !== targetContainer) {
        targetContainer.scrollTop = activeContainer.scrollTop;
      }
    },
    [swiper],
  );

  // 슬라이드 스크롤 영역 ref 등록
  // 새로 그려진 슬라이드는 scrollTop 0(00:00)에서 시작하므로 현재 시각 위치로 즉시 맞춘다.
  // (보고 있는 슬라이드와 위치가 다르면 전환 직전 syncScrollTop이 다시 맞춘다)
  const registerScrollContainer = (index: number, el: HTMLDivElement | null) => {
    scrollContainerRefs.current[index] = el;
    if (!el || initializedScrollContainersRef.current.has(el)) return;

    initializedScrollContainersRef.current.add(el);
    el.scrollTop = getCurrentTimeScrollTop(el);
  };

  // 자동 슬라이드 전환 함수 (다음 슬라이드로 이동)
  const moveToNextSlide = useCallback(() => {
    if (!swiper || isManualNavigation) return;

    const nextPage = currentPage + 1;
    const targetPage = totalPages && nextPage > totalPages ? 1 : nextPage;
    // setIsManualNavigation(true);
    setCurrentPage(targetPage);

    swiper.update();

    setTimeout(() => {
      syncScrollTop(targetPage - 1);
      swiper.slideTo(targetPage - 1, SLIDE_SPEED);

      setTimeout(() => {
        // setIsManualNavigation(false);
        scrollToCurrentTime();
        swiper.update();
        // setAllFacilityChunks([...allFacilityChunks]);
      }, SLIDE_SPEED + 100);
    }, 50); // 미세한 지연으로 렌더링 사이클 보장
  }, [swiper, isManualNavigation, currentPage, totalPages, scrollToCurrentTime, syncScrollTop]);

  const dataRefreshTimer = useCountdown(TIME_INTERVAL / 1000, handleDataRefresh);
  const autoplayTimer = useCountdown(AUTOPLAY_DELAY / 1000, moveToNextSlide);
  const scrollTimer = useCountdown(TIME_INTERVAL / 1000, scrollToCurrentTime);

  // 날짜 복원 카운트다운 (30초)
  const dateRestoreTimer = useCountdown(DATE_INACTIVITY_TIMEOUT / 1000, () => {
    // 복원은 1회만 수행한다. 멈추지 않으면 오늘로 돌아온 뒤에도 30초마다 URL 이동·스크롤이 반복된다.
    // (다른 날짜를 다시 선택하면 각 날짜 핸들러에서 start() 된다)
    dateRestoreTimer.stop();

    // 오늘 날짜로 복원. 데이터 재호출은 다른 날짜 핸들러와 동일하게
    // selectedDate 변경 → queryKey 변경으로 useQuery가 처리한다.
    setSelectedDate(new Date());
    // URL에서 selectedDate 제거
    const currentQueryParams = qs.parse(location.search, {
      ignoreQueryPrefix: true,
      allowDots: true,
    });
    delete currentQueryParams.selectedDate;
    const newQueryParamStr = qs.stringify(currentQueryParams, { allowDots: true, encode: true });
    // 자동 복원이므로 history를 쌓지 않도록 replace
    navigate(location.pathname + (newQueryParamStr ? "?" + decodeURIComponent(newQueryParamStr) : ""), { replace: true });

    // 복원 후 타임라인을 현재 시각 위치로 스크롤 (스크롤 위치는 예약 데이터와 무관)
    setTimeout(() => {
      scrollToCurrentTime();
    }, 300);
  });

  // 모달 자동 닫기 카운트다운 (60초)
  const modalInactivityTimer = useCountdown(MODAL_INACTIVITY_TIMEOUT / 1000, () => {
    setReservationSettingModal({ isOpen: false });
    setReservationDetailModal({ isOpen: false });
  });

  // 모달 헨들러
  const handleReservationDetailModal = (type: string, payload?: any) => {
    scrollTimer.reset();
    modalInactivityTimer.stop();

    if (isAutoplayEnabled) {
      autoplayTimer.start();
    } else {
      scrollTimer.start();
    }

    if (type === "open") {
      if (!isAutoplayEnabled) {
        setReservationDetailModal({ isOpen: true, payload });
        modalInactivityTimer.start();
      }
    }
    if (type === "close") setReservationDetailModal({ isOpen: false });
  };

  const handleCloseReservationSettingModal = () => {
    modalInactivityTimer.stop();

    if (isAutoplayEnabled) {
      autoplayTimer.start();
    } else {
      scrollTimer.start();
    }
    setReservationSettingModal({ isOpen: false });
  };

  // 사용자 인터랙션 처리 (스로틀링 적용)
  const handleUserInteraction = useCallback(
    throttle((event) => {
      if (event.type === "wheel") {
        const mouseX = event.clientX;
        const mouseY = event.clientY;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // 경계 근처 확인
        const isTooCloseToEdge =
          mouseX <= SAFE_MARGIN || mouseX >= viewportWidth - SAFE_MARGIN || mouseY <= SAFE_MARGIN || mouseY >= viewportHeight - SAFE_MARGIN;

        if (isTooCloseToEdge) {
          return; // 경계 근처면 이벤트 무시
        }
      }

      if (isAutoplayEnabled) {
        autoplayTimer.reset();
      } else {
        scrollTimer.reset();
      }

      // 날짜 복원 타이머도 리셋 (선택된 날짜가 오늘이 아닐 때만)
      if (!moment(selectedDate).isSame(moment(), "day")) {
        dateRestoreTimer.reset();
      }

      // dataRefreshTimer.reset();
    }, 100), // 100ms 스로틀링
    [isAutoplayEnabled, selectedDate, dateRestoreTimer],
  );

  const resetDateRestoreTimer = useCallback(() => {
    // 선택된 날짜가 오늘이 아닐 때만 타이머 리셋
    if (!moment(selectedDate).isSame(moment(), "day")) {
      dateRestoreTimer.reset();
    }
  }, [selectedDate, dateRestoreTimer]);

  // 날짜 클릭 핸들러
  const handleDateClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDatePicker(true);
  }, []);

  // 이전 날짜 이동 핸들러
  const handlePrevDate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const prevDate = moment(selectedDate).subtract(1, "day").toDate();
      setSelectedDate(prevDate);

      const prevDateStr = moment(prevDate).format("YYYY-MM-DD");
      navigateWithQueryParams({ ...queryParams, selectedDate: prevDateStr });

      // 선택된 날짜가 오늘이 아닌 경우에만 타이머 시작
      if (!moment(prevDate).isSame(moment(), "day")) {
        setTimeout(() => {
          dateRestoreTimer.start();
        }, 100);
      }
    },
    [selectedDate, queryParams, navigateWithQueryParams, dateRestoreTimer],
  );

  // 다음 날짜 이동 핸들러
  const handleNextDate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const nextDate = moment(selectedDate).add(1, "day").toDate();
      setSelectedDate(nextDate);

      const nextDateStr = moment(nextDate).format("YYYY-MM-DD");
      navigateWithQueryParams({ ...queryParams, selectedDate: nextDateStr });

      // 선택된 날짜가 오늘이 아닌 경우에만 타이머 시작
      if (!moment(nextDate).isSame(moment(), "day")) {
        setTimeout(() => {
          dateRestoreTimer.start();
        }, 100);
      }
    },
    [selectedDate, queryParams, navigateWithQueryParams, dateRestoreTimer],
  );

  // 날짜 선택 핸들러
  const handleDateSelect = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      setShowDatePicker(false);

      const selectedDateStr = moment(date).format("YYYY-MM-DD");
      navigateWithQueryParams({ ...queryParams, selectedDate: selectedDateStr });

      // 선택된 날짜가 오늘이 아닌 경우에만 타이머 시작
      if (!moment(date).isSame(moment(), "day")) {
        // 약간의 지연 후 타이머 시작 (상태 업데이트 후)
        setTimeout(() => {
          dateRestoreTimer.start();
        }, 100);
      }
    },
    [queryParams, navigateWithQueryParams, dateRestoreTimer],
  );

  // 날짜 피커 외부 클릭시 닫기
  const handleOutsideClick = useCallback((e: MouseEvent) => {
    const target = e.target as Element;
    if (!target.closest(".date-display-container")) {
      setShowDatePicker(false);
    }
  }, []);

  // convertDisplayDate를 선택된 날짜에 따라 업데이트
  const getDisplayDate = useCallback(() => {
    const targetDate = selectedDate;
    const ymd = moment(targetDate).format(`YYYY.MM.DD`);
    const week = moment(targetDate).format(`ddd`);
    const days = ["월", "화", "수", "목", "금", "토", "일"];
    const englishDays = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

    const index = days.indexOf(week);
    const convertWeek = index !== -1 ? englishDays[index] : week;

    return `${ymd}(${convertWeek})`;
  }, [selectedDate]);

  //네비게이션 버튼 공통 로직
  const handleNavigation = useCallback(
    (direction: string) => {
      const newPage = direction === "prev" ? currentPage - 1 : currentPage + 1;
      if (newPage <= 0 || (totalPages && newPage > totalPages)) {
        return;
      }

      setCurrentPage(newPage);
      setIsManualNavigation(true);

      if (swiper) {
        swiper.update();
        syncScrollTop(newPage - 1);
        swiper.slideTo(newPage - 1, SLIDE_SPEED);

        if (isAutoplayEnabled) {
          autoplayTimer.reset();
        }

        setTimeout(() => {
          setTimeout(() => {
            setIsManualNavigation(false);
            scrollToCurrentTime();
          }, 100);
        }, SLIDE_SPEED);
      }
    },
    [currentPage, totalPages, swiper, isAutoplayEnabled, autoplayTimer, scrollToCurrentTime, syncScrollTop],
  );

  // 네비게이션 버튼 핸들러
  const handlePrevSlide = useCallback(() => handleNavigation("prev"), [handleNavigation]);
  const handleNextSlide = useCallback(() => handleNavigation("next"), [handleNavigation]);

  // 최초 데이터 로드 및 타이머 설정
  useEffect(() => {
    fetchCommonfacility();
    scrollToCurrentTime();

    dataRefreshTimer.start();
    if (isAutoplayEnabled) {
      autoplayTimer.start();
    } else {
      scrollTimer.start();
    }

    // 쿼리파라미터에서 선택된 날짜 초기화
    if (queryParams.selectedDate) {
      const selectedDateFromQuery = moment(queryParams.selectedDate).toDate();
      setSelectedDate(selectedDateFromQuery);

      // 선택된 날짜가 오늘이 아니면 날짜 복원 타이머 시작
      if (!moment(selectedDateFromQuery).isSame(moment(), "day")) {
        setTimeout(() => {
          dateRestoreTimer.start();
        }, 1000); // 1초 후 시작 (초기화 완료 후)
      }
    }
  }, []);

  // 예약 데이터 호출은 useQuery가 담당한다.
  // facilityList 최초 로드 및 날짜/필터 변경 시 queryKey가 바뀌어 조회되고, 30초 갱신은 handleDataRefresh에서 refetch한다.

  useEffect(() => {
    if (swiper) {
      swiper.update();
      scrollToCurrentTime();
    }
  }, [swiper, scrollToCurrentTime]);

  useEffect(() => {
    const userInteractionEvents = ["wheel"];
    const eventHandlers: { [key: string]: EventListener } = {};
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        scrollToCurrentTime();

        if (isAutoplayEnabled) {
          autoplayTimer.reset();
        } else {
          scrollTimer.reset();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    userInteractionEvents.forEach((eventType) => {
      eventHandlers[eventType] = (e) => handleUserInteraction(e);
      window.addEventListener(eventType, eventHandlers[eventType]);
    });

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      userInteractionEvents.forEach((eventType) => {
        window.removeEventListener(eventType, eventHandlers[eventType]);
      });
    };
    // dataRefreshTimer는 이 effect 내부에서 사용되지 않아 deps에서 제외
  }, [scrollToCurrentTime, handleUserInteraction, isAutoplayEnabled, autoplayTimer, scrollTimer]);

  useEffect(() => {
    // mousemove 제거 - 의미있는 인터랙션만 감지
    const userInteractionEvents = ["mousedown", "keydown", "touchstart", "click"];
    const eventHandlers: { [key: string]: EventListener } = {};

    const handleUserInteractionForModal = (event: Event) => {
      // wheel은 이 effect의 등록 이벤트(mousedown/keydown/touchstart/click)에 포함되지 않으므로
      // 휠 경계 무시 로직은 :725 effect의 handleUserInteraction에서 처리됨
      if (reservationSettingModal.isOpen || reservationDetailModal.isOpen) {
        modalInactivityTimer.reset();
      }

      // 날짜 복원 타이머 리셋 (모달이 열려있지 않을 때만)
      if (!reservationSettingModal.isOpen && !reservationDetailModal.isOpen) {
        resetDateRestoreTimer();
      }
    };

    userInteractionEvents.forEach((eventType) => {
      eventHandlers[eventType] = handleUserInteractionForModal;
      window.addEventListener(eventType, eventHandlers[eventType]);
    });

    // 날짜 피커 외부 클릭 이벤트 추가
    if (showDatePicker) {
      document.addEventListener("click", handleOutsideClick);
    }

    return () => {
      userInteractionEvents.forEach((eventType) => {
        window.removeEventListener(eventType, eventHandlers[eventType]);
      });

      // 날짜 피커 외부 클릭 이벤트 제거
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [
    reservationSettingModal.isOpen,
    reservationDetailModal.isOpen,
    modalInactivityTimer,
    resetDateRestoreTimer,
    showDatePicker,
    handleOutsideClick,
  ]);

  // queryParams 값에 따라 자동슬라이드여부 결정
  const swiperParams = {
    initialSlide: 0,
    threshold: 40, // (40px 이상 드래그해야 작동)
    onSwiper: (swiperInstance: SwiperType) => {
      if (swiperInstance) {
        setSwiper(swiperInstance);
        swiperInstance.update();

        // 이벤트 리스너 명시적 등록 (메모리 누수 방지를 위해 먼저 이벤트 제거)
        swiperInstance.off("slideChangeTransitionEnd");
        swiperInstance.off("slideChange");

        swiperInstance.on("slideChangeTransitionEnd", () => {
          // onSwiper는 최초 1회만 호출되므로 등록 시점의 함수가 아닌 최신 함수를 호출한다
          // (등록 시점 함수는 swiper가 null이고 현재 시각도 마운트 시점 값이라 엉뚱한 위치로 스크롤됨)
          scrollToCurrentTimeRef.current();

          if (isAutoplayEnabled) {
            autoplayTimer.reset();
          } else {
            scrollTimer.reset();
          }
          swiperInstance.update();
        });
        // slideChange: 슬라이드가 변경될 때마다 발생하는 이벤트
        swiperInstance.on("slideChange", () => {
          const currentIndex = swiperInstance.activeIndex;
          const newPage = currentIndex + 1;

          if (!isManualNavigation) {
            setCurrentPage(newPage);
          }
          swiperInstance.update();
        });
      }
    },

    loop: false,
    autoplay: false,
    slidesPerView: 1,
    allowSlideNext: true,
    allowSlidePrev: true,
    simulateTouch: !isAutoplayEnabled,
    allowTouchMove: false, // 터치 이동 허용
    speed: SLIDE_SPEED, // 슬라이딩 애니메이션 속도 설정 (밀리초)
  };

  // 임시 하드코딩 로고 출력함수
  const getLogoClass = (partnerId: number | undefined) => {
    switch (process.env.REACT_APP_ENVIRONMENT) {
      case "prod":
        if (Number(partnerId) === 17) {
          return "fact";
        } else if (Number(partnerId) === 21) {
          return "gs";
        } else if (Number(partnerId) === 1) {
          return "pinpoint";
        } else return;
      case "dev":
        if (Number(partnerId) === 257) {
          return "pinpoint";
        } else if (Number(partnerId) === 307) {
          return "gs";
        }
    }
  };

  const handleOpenReservationSettingModal = () => {
    setReservationSettingModal({ isOpen: true });

    // Start the inactivity timer
    modalInactivityTimer.start();
  };

  return (
    <div className="reservation-calendar-page">
      {isReservationFetching && (
        <div className="reservation-calendar-loading">
          <div className="loader"></div>
        </div>
      )}
      <div className="logo-area">
        <div className="logo-area__left">
          <div className={`logo-area__left-logo ${getLogoClass(partner?.id)}`}></div>
          <h2>{t("reservation.display.body.title")}</h2>
        </div>
        <div className="logo-area__right">
          <div className="date-display-container" style={{ position: "relative" }}>
            <button className="date-display-button left" onClick={handlePrevDate}></button>
            <div onClick={handleDateClick} className="reservation-calendar-date-display">
              <p>{getDisplayDate()}</p> <p className="display-ic-calendar"></p>
            </div>
            <button className="date-display-button right" onClick={handleNextDate}></button>
            {showDatePicker && (
              <div className="reservation-calendar-datepicker">
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateSelect}
                  locale={ko}
                  dateFormat="yyyy.MM.dd"
                  inline
                  calendarClassName="base-datepicker"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="reservation-calendar">
        <Swiper {...swiperParams} className="reservation-calendar-swiper">
          {Array.from({ length: totalPages }).map((_, index) => {
            // 현재 슬라이드가 가시 범위 내에 있는지 확인
            const pageIndex = currentPage - 1;
            const visibleRange = 2;
            const distance = Math.abs(index - pageIndex);
            // 자동 전환의 마지막 ↔ 첫 페이지 순환 시에도 전환 전후 슬라이드가 그려져 있도록 반대편 끝 슬라이드 1개를 함께 그린다
            // (수동 모드는 양 끝에서 버튼이 비활성화되어 순환하지 않음)
            const isWrapNeighbor = isAutoplayEnabled && totalPages - distance === 1;
            const isVisible = distance <= visibleRange || isWrapNeighbor;
            const slideData = allFacilityChunks[index] || Array(6).fill({} as CommonFacilityListModel);
            return (
              <SwiperSlide key={`slide-${index}`}>
                {isVisible ? (
                  <>
                    <div className="reservation-calendar-header">
                      <div className="reservation-calendar-header-content">
                        <div className="reservation-calendar-header-qr">
                          <div className="qr-wrap">
                            <div className="qr">
                              <QRCode
                                value={`${process.env.REACT_APP_COURT_BASSEURL}/front/taap/reservation/room?facilityType=MEETING_ROOM` || ""}
                                size={80}
                              />
                              {browserLanguage === "en" ? (
                                <div className="qr-info en">
                                  <p>Book via</p>
                                </div>
                              ) : queryParams.lang === "en" ? (
                                <div className="qr-info en">
                                  <p>Book via</p>
                                </div>
                              ) : (
                                <div className="qr-info">
                                  <p>에서 예약</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="reservation-calendar-header-rooms">
                          {slideData.length > 0
                            ? slideData.map((room, index) => (
                                <div key={room.id + "-" + index} className="reservation-calendar-header-room">
                                  <div
                                    className={`reservation-calendar-header-room-box ${
                                      room.buildingName && !room.mediaList?.[0]?.url
                                        ? "empty-images"
                                        : !room.buildingName && !room.mediaList?.[0]?.url
                                        ? "empty-facility"
                                        : ""
                                    }`}
                                    {...(room.mediaList?.[0]?.url
                                      ? { style: { backgroundImage: `url(${resizedImageUrl(room.mediaList?.[0]?.url)})` } }
                                      : {})}
                                  >
                                    <div className="reservation-calendar-header-room-capacity">
                                      {(() => {
                                        const maxPeople = room?.facilityMaxPeopleNums || 0;
                                        return (
                                          maxPeople > 0 && (
                                            <div className="capacity-chip">
                                              <p className="number">{maxPeople}</p>
                                            </div>
                                          )
                                        );
                                      })()}
                                    </div>
                                    <div className="reservation-calendar-header-room-name">{room?.facilityName || ""}</div>
                                  </div>
                                </div>
                              ))
                            : Array.from({ length: 6 }).map((_, index) => (
                                <div className="reservation-calendar-header-room" key={`empty-room-${index}`}>
                                  <div className="reservation-calendar-header-room-box empty-facility"></div>
                                </div>
                              ))}
                        </div>
                      </div>
                    </div>

                    <div className="reservation-calendar-content">
                      <div
                        className="reservation-calendar-content-scroll"
                        ref={(el) => registerScrollContainer(index, el)}
                        style={isAutoplayEnabled ? { overflowY: "hidden", marginRight: "28px" } : {}}
                      >
                        <div className="reservation-calendar-time">
                          {timeSlots.map((time) => (
                            <div key={time} className="reservation-calendar-time-slot">
                              <p>{time !== "24:30" ? time : ""}</p>
                            </div>
                          ))}
                        </div>
                        <div className="reservation-calendar-grid">
                          {slideData.length > 0
                            ? slideData.map((facility, index) => (
                                <div
                                  key={facility.id + "-" + index}
                                  className={`reservation-calendar-grid-column ${facility.id ? "" : "empty-column"}`}
                                >
                                  {timeSlots.map((time) => {
                                    return (
                                      <div key={`${facility.id}-${time}`} className="reservation-calendar-grid-column-cell">
                                        <ReservationBox
                                          timeSlotsTime={time}
                                          facility={facility}
                                          currentPage={currentPage}
                                          handleReservationDetailModal={handleReservationDetailModal}
                                          getCurrentTimePosition={getCurrentTimePosition}
                                          selectedDate={selectedDate}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              ))
                            : Array.from({ length: 6 }).map((_, index) => (
                                <div key={`empty-${index}`} className="reservation-calendar-grid-column">
                                  {timeSlots.map((time, tIndex) => (
                                    <div key={`empty-${index}-${time}`} className="reservation-calendar-grid-column-cell"></div>
                                  ))}
                                </div>
                              ))}
                        </div>
                        <div className="reservation-calendar-current-time" style={{ top: `${getCurrentTimePosition}px` }}>
                          <div className="reservation-calendar-current-time-line" />
                          <div className="reservation-calendar-current-time-label">{moment(currentTime).format(ViewYmdFormat.HH_MM)}</div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <></>
                )}
              </SwiperSlide>
            );
          })}
        </Swiper>

        <div className="reservation-calendar-navigation">
          {!isAutoplayEnabled && (
            <button className="reservation-calendar-navigation-button" disabled={currentPage === 1} onClick={handlePrevSlide}></button>
          )}
          <div className="reservation-calendar-navigation-page">
            <b>{`${currentPage}`}</b>
            <span className="mx2">/</span>
            {totalPages ? `${totalPages}` : "1"}
          </div>
          {!isAutoplayEnabled && (
            <button className="reservation-calendar-navigation-button" disabled={currentPage === totalPages} onClick={handleNextSlide}></button>
          )}
        </div>

        {process.env.REACT_APP_ENVIRONMENT === "dev" && (
          <div className="reservation-calendar-extra-left">
            <div className="reservation-calendar-countdowns font20 flex-center gap10">
              <div className="reservation-calendar-countdown ">
                <span className="countdown-label">데이터자동갱신:</span>
                <span className="countdown-time">
                  {" "}
                  <CountdownText timer={dataRefreshTimer} />초
                </span>
              </div>
              {isAutoplayEnabled && (
                <div className="reservation-calendar-countdown">
                  <span className="countdown-label">자동슬라이드:</span>
                  <span className="countdown-time">
                    <CountdownText timer={autoplayTimer} />초
                  </span>
                </div>
              )}
              {!isAutoplayEnabled && (
                <div className="reservation-calendar-countdown">
                  <span className="countdown-label">자동스크롤:</span>
                  <span className="countdown-time">
                    <CountdownText timer={scrollTimer} />초
                  </span>
                </div>
              )}
              {!moment(selectedDate).isSame(moment(), "day") && (
                <div className="reservation-calendar-countdown">
                  <span className="countdown-label">오늘날짜복원:</span>
                  <span className="countdown-time">
                    <CountdownText timer={dateRestoreTimer} />초
                  </span>
                </div>
              )}
              {(reservationSettingModal.isOpen || reservationDetailModal.isOpen) && (
                <div className="reservation-calendar-countdown">
                  <span className="countdown-label">모달자동닫기:</span>
                  <span className="countdown-time">
                    <CountdownText timer={modalInactivityTimer} />초
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="reservation-calendar-extra">
          <p className="extra-notice">{t("reservation.display.toast.title")}</p>
          <button type="button" aria-label="페이지 새로고침" className="reservation-calendar-extra-reload" onClick={() => window.location.reload()} />
          <button className="reservation-calendar-extra-setting" onClick={handleOpenReservationSettingModal}></button>
          <button
            className="reservation-calendar-extra-language"
            onClick={() => {
              const passParams = { ...queryParams };
              if (browserLanguage === "en") {
                return;
              } else if (queryParams.lang === "en") {
                passParams.lang = "kr";
              } else if (queryParams.lang === "kr") {
                passParams.lang = "en";
              } else {
                passParams.lang = "en";
              }
              navigateWithQueryParams(passParams);
              window.location.reload();
            }}
          >
            {browserLanguage === "en" ? "EN" : queryParams.lang === "en" ? "EN" : "KR"}
          </button>
        </div>
      </div>
      {reservationDetailModal.isOpen && (
        <ReservationDetailModal reservation={reservationDetailModal.payload} onClose={() => handleReservationDetailModal("close")} />
      )}
      {reservationSettingModal.isOpen && (
        <ReservationSettingModal
          data={facilityList}
          selectedBuildings={(value) => {
            const newPage = 1;
            navigateWithQueryParams({ ...queryParams, buildingIdList: value, floor: "" });
            setCurrentPage(newPage);
            if (swiper) {
              swiper.slideTo(newPage - 1, 0);
              swiper.update();
            }
            // fetchCommonfacility().then(() => {
            //   fetchReservationList(newPage, { ...queryParams, buildingIdList: value, floor: "" });
            // });
          }}
          selectedFloor={(value) => {
            const newPage = 1;
            navigateWithQueryParams({ ...queryParams, floor: value });
            setCurrentPage(newPage);
            if (swiper) {
              swiper.slideTo(newPage - 1, 0);
              swiper.update();
            }
            // fetchCommonfacility().then(() => {
            //   fetchReservationList(newPage, { ...queryParams, buildingIdList: value, floor: "" });
            // });
          }}
          selectedFacilityType={(value) => {
            const newPage = 1;
            navigateWithQueryParams({ ...queryParams, commonFacilityTypeList: value });
            setCurrentPage(newPage);
            if (swiper) {
              swiper.slideTo(newPage - 1, 0);
              swiper.update();
            }
            // fetchCommonfacility().then(() => {
            //   fetchReservationList(newPage, { ...queryParams, buildingIdList: value, floor: "" });
            // });
          }}
          onClose={handleCloseReservationSettingModal}
        />
      )}
    </div>
  );
};

export default ReservationCalendar;
